import axios from "axios";
import { useEffect, useState } from "react";
import { Button, Modal } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useFetchCancellationPolicyQuery } from "../api/fetchCancellationPolicy";
import { useSelector } from "react-redux";
import { REACT_APP_API_URL } from "../env";
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import "./cancellation.css";
import { parse, isBefore, isAfter, isSameDay, isWithinInterval } from 'date-fns';

const cancellationPolicy = [
  { id: 1, window: "15 or more days", deduction: "20%", description: "20% deduction if cancelled 15 or more days before check-in Date" },
  { id: 2, window: "7 to 14 days", deduction: "30%", description: "30% deduction if cancelled between 7 to 14 days before check-in Date" },
  { id: 3, window: "2 to 6 days", deduction: "50%", description: "50% deduction if cancelled between 2 to 6 days before check-in Date" },
  { id: 4, window: "Less than 2 days", deduction: "No cancellation allowed", description: "No cancellation allowed within 2 days of check-in Date", isCritical: true },
];

const Cancellation = () => {
  let { bookingId } = useParams();
  const { user } = useSelector((state) => state.auth);
  const userId = user?.customerId;
  const [cancellationType, setCancellationType] = useState("");
  const [partialCancellationTable, setPartialCancellationTable] = useState([]);
  const { data, isLoading, refetch } = useFetchCancellationPolicyQuery(bookingId);
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [originalRooms, setOriginalRooms] = useState([]);
  const [workingRooms, setWorkingRooms] = useState([]);
  const [refundAmount, setRefundAmount] = useState(0);
  const [cancellationAmount, setCancellationAmount] = useState(0);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [selectedRoomConfig, setSelectedRoomConfig] = useState(null);
  const [selectedRoomID, setSelectedRoomID] = useState(null);
  const [cancelledRooms, setCancelledRooms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [cancelledRoomIds, setCancelledRoomIds] = useState([]);
  const [partialCancellationStarted, setPartialCancellationStarted] = useState(false);
  const [cancellationInfo, setCancellationInfo] = useState(null);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [modifyBookingData, setModifyBookingData] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(null);
  const [globalModifyDates, setGlobalModifyDates] = useState({ newCheckinDate: '', newCheckoutDate: '' });
  const [partialDayCancellationEligible, setPartialDayCancellationEligible] = useState(false);
  const [hasPartialRoomCancellation, setHasPartialRoomCancellation] = useState(false);
  const [hasPartialDayCancellation, setHasPartialDayCancellation] = useState(false);

  const [taxRate, setTaxRate] = useState(null);
  const getTaxRate = (bookingDate) => {
    const cutoffDate = '2025-09-22T00:00:00.000Z';

    return bookingDate < cutoffDate ? 0.12 : 0.05;
  };



  function parseDMY(dateStr) {
    if (!dateStr) return null;
    const [day, mon, year] = dateStr.split("-");
    const months = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
    };
    return new Date(Number(year), months[mon], Number(day));
  }

  // Convert DMY format to YYYY-MM-DD for date input
  function convertDMYtoDateInput(dateStr) {
    if (!dateStr) return '';
    const [day, mon, year] = dateStr.split("-");
    const months = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
    };
    return `${year}-${months[mon]}-${day.padStart(2, '0')}`;
  }

  // Convert YYYY-MM-DD back to DMY format
  function convertDateInputToDMY(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate().toString();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const mon = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${mon}-${year}`;
  }

  const totalDaysBooked = workingRooms.reduce((sum, room) => {
    const inDate = parseDMY(room.CheckinDate);
    const outDate = parseDMY(room.CheckOutDate);
    const numDays = (outDate - inDate) / (1000 * 60 * 60 * 24);

    return numDays;
  }, 0);

  const bookingDuration = originalRooms.length > 0 ? (() => {
    const room = originalRooms[0];
    const inDate = parseDMY(room?.CheckinDate);
    const outDate = parseDMY(room?.CheckOutDate);
    return (outDate - inDate) / (1000 * 60 * 60 * 24);
  })() : 0;


  useEffect(() => {
    let isMounted = true;

    const fetchCancelledRoomIds = async () => {
      try {
        const response = await axios.get(
          `${REACT_APP_API_URL}/api/hotel/get-cancellation-details/${bookingId}`
        );

        if (isMounted) {
          const roomIds = response.data
            .map((item) =>
              item.vchRoomIds
                ? item.vchRoomIds
                  .split(',')
                  .map((id) => parseInt(id.trim()))
                : []
            )
            .flat();
          setCancelledRoomIds(roomIds);
          setPartialCancellationStarted(roomIds.length > 0);
        }
      } catch (error) {
        if (isMounted && error.response?.status !== 404) {
          console.error("Error fetching cancellation details:", error);
        }
      }
    };

    if (bookingId && !isLoading) {
      fetchCancelledRoomIds();
    }

    return () => {
      isMounted = false;
    };
  }, [bookingId, isLoading]);

  useEffect(() => {
    if (data?.booking) {
      const allRooms = data.booking.flatMap((booking, bookingIndex) =>
        Array.from({ length: booking.TotalRoom }, (_, roomIndex) => ({
          ...booking,
          uniqueId: `${bookingIndex}-${roomIndex}`,
        }))
      );
      setOriginalRooms(allRooms);
      setWorkingRooms(allRooms);
    }

    // Set partial day cancellation eligibility from API response
    if (data?.partialDayCancellationEligible !== undefined) {
      setPartialDayCancellationEligible(data.partialDayCancellationEligible);
    }

    // flags--> user has already started a cancellation method
    if (data?.hasPartialRoomCancellation !== undefined) {
      setHasPartialRoomCancellation(data.hasPartialRoomCancellation);
    }
    if (data?.hasPartialDayCancellation !== undefined) {
      setHasPartialDayCancellation(data.hasPartialDayCancellation);
    }
  }, [data]);

  const getAvailableCancellationType = () => {
    // If user has already started partial room cancellation, lock them to it
    if (hasPartialRoomCancellation) {
      return "partial";
    }

    // If user has started partial day cancellation, default to it but allow switching to full
    if (hasPartialDayCancellation) {
      return "modify";
    }

    // If no lock is active user can select all dropdown menu
    return "";
  };

  // Refetch latest cancellation data
  const fetchLatestCancellationData = async () => {
    try {
      await refetch();
    } catch (error) {
      console.error("Error refetching cancellation details:", error);
    }
  };

  const handleCancellationTypeChange = async (type) => {
    setCancellationType(type);
    setWorkingRooms(originalRooms);



    if (type === "modify") {
      // Initialize global modify dates with original check-in dates from first room
      if (originalRooms.length > 0) {
        const firstRoom = originalRooms[0];
        setGlobalModifyDates({
          newCheckinDate: convertDMYtoDateInput(firstRoom.CheckinDate),
          newCheckoutDate: convertDMYtoDateInput(firstRoom.CheckOutDate),
          originalCheckinDate: firstRoom.CheckinDate,
          originalCheckoutDate: firstRoom.CheckOutDate
        });
      }
    } else
      if (type === "partial") {
        try {
          const response = await axios.post(
            `${REACT_APP_API_URL}/api/hotel/cancel/partial/${bookingId}`
          );
          setPartialCancellationTable(response.data);
        } catch (error) {
          toast.error("Error while fetching cancellation policy");
        }
      }
  };


  //open modal for full cancellation along with preview for charges 
  const handleOpenModal = async () => {
    setShowModal(true);
    setLoadingCharges(true);

    try {
      const res = await axios.get(`${REACT_APP_API_URL}/api/hotel/cancel-preview/${bookingId}`);
      setCancellationInfo(res.data.cancellationData);
    } catch (error) {
      console.error("Failed to fetch cancellation preview", error);
    } finally {
      setLoadingCharges(false);
    }
  };


  // Function to handle full cancellation after preview 
  const handleFullCancellation = async () => {
    try {
      const response = await axios.post(`${REACT_APP_API_URL}/api/hotel/cancel-all/${bookingId}`);
      if (response.status === 200) {
        const cancelledText = workingRooms.length > 1 ? "All rooms cancelled successfully" : "Room cancelled successfully";
        toast.success(cancelledText);

        setCancelledRooms(originalRooms.map((room) => room.RoomType));
        setWorkingRooms([]);
        setCancelledRoomIds(originalRooms.map((room) => room.RoomType));

        setTimeout(() => {
          navigate("/booking-history");
        }, 5000);
      }
    } catch (error) {
      toast.error("Error while cancelling booking");
    } finally {
      setShowModal(false);
    }
  };

  // Function to handle partial cancellation
  const handleRoomCancellation = async (price, roomconfig, ROOMID) => {
    console.log('pricce', price)
    setSelectedPrice(price);
    setSelectedRoomConfig(roomconfig);
    setSelectedRoomID(ROOMID);
    setCancellationAmount(null);
    setShowCancelModal(true);

    try {
      const response = await axios.post(`${REACT_APP_API_URL}/api/hotel/cancel/partial/${bookingId}`, {
        Price: price,
        RoomConfig: roomconfig,
        UserID: userId,
        ROOMID: ROOMID
      });

      if (response.status === 200) {
        const cancelAmt = response.data.cancellationData[0].Amount;
        setCancellationAmount(cancelAmt);
        setRefundAmount(price + (price * taxRate) - cancelAmt);
      }
    } catch (error) {
      toast.error("Error while calculating cancellation charges");
    }
  };

  // Function to confirm cancellation
  const confirmCancellation = async () => {
    if (!selectedPrice || !selectedRoomConfig || !userId) {
      toast.error("Missing data for cancellation confirmation");
      return;
    }

    try {
      const cancelResponse = await axios.post(`${REACT_APP_API_URL}/api/hotel/cancel-room/confirm/${bookingId}`, {
        Price: selectedPrice,
        RoomConfig: selectedRoomConfig,
        UserID: userId,
        ROOMID: selectedRoomID
      });

      if (cancelResponse.status === 200) {
        // Update cancelled room list 
        setCancelledRoomIds((prev) => [...prev, selectedRoomID]);
        // Remove cancelled room from working rooms
        const updatedWorkingRooms = workingRooms.filter(room => room.RoomBookingID !== selectedRoomID);
        setWorkingRooms(updatedWorkingRooms);

        // Refresh cancellation info
        await fetchLatestCancellationData();
        const successMessage = updatedWorkingRooms.length > 0
          ? "Room cancelled successfully"
          : "All rooms cancelled successfully";

        toast.success(successMessage);

        if (updatedWorkingRooms.length === 0) {
          // All rooms cancelled 
          setTimeout(() => {
            navigate("/booking-history");
          }, 5000);
        }

        setShowCancelModal(false);
      }
    } catch (error) {
      console.error("Error in confirmation:", error);
      toast.error("Error while confirming cancellation");
    }
  };

  useEffect(() => {
    const allowedType = getAvailableCancellationType();
    setCancellationType((prev) => (prev === allowedType ? prev : allowedType));
  }, [originalRooms, partialCancellationStarted, bookingDuration, partialDayCancellationEligible, hasPartialRoomCancellation, hasPartialDayCancellation]);

  useEffect(() => {
    if (cancellationType === "modify" && originalRooms.length > 0) {
      const firstRoom = originalRooms[0];
      setGlobalModifyDates((prev) => {
        if (prev?.originalCheckinDate && prev?.originalCheckoutDate) {
          return prev;
        }
        return {
          newCheckinDate: convertDMYtoDateInput(firstRoom.CheckinDate),
          newCheckoutDate: convertDMYtoDateInput(firstRoom.CheckOutDate),
          originalCheckinDate: firstRoom.CheckinDate,
          originalCheckoutDate: firstRoom.CheckOutDate
        };
      });
    }
  }, [cancellationType, originalRooms]);

  useEffect(() => {
    if (data?.booking?.[0]?.BookingDate) {
      const bookingDate = data.booking[0].BookingDate;
      const rate = getTaxRate(bookingDate);
      setTaxRate(rate);
    }
  }, [data]);

  useEffect(() => {
    setPartialCancellationStarted(cancelledRoomIds.length > 0);
  }, [cancelledRoomIds]);

  // Handle update all bookings
  const handleUpdateAllBookings = async () => {
    // Validate global dates
    const newCheckinDMY = convertDateInputToDMY(globalModifyDates.newCheckinDate);
    const newCheckoutDMY = convertDateInputToDMY(globalModifyDates.newCheckoutDate);

    if (!newCheckinDMY || !newCheckoutDMY) {
      toast.error(`Please select valid dates`);
      return;
    }

    const inDateObj = parseDMY(newCheckinDMY);
    const outDateObj = parseDMY(newCheckoutDMY);
    if (inDateObj >= outDateObj) {
      toast.error(`Check-out date must be after check-in date`);
      return;
    }

    // Process all room modifications
    try {
      let successCount = 0;
      let errorCount = 0;
      let lastErrorMessage = "";

      for (const room of workingRooms) {
        const newCheckinDMY = convertDateInputToDMY(globalModifyDates.newCheckinDate);
        const newCheckoutDMY = convertDateInputToDMY(globalModifyDates.newCheckoutDate);

        try {
          const response = await axios.post(
            `${REACT_APP_API_URL}/api/hotel/modify-booking/${bookingId}`,
            {
              newCheckinDate: newCheckinDMY,
              newCheckoutDate: newCheckoutDMY,
              roomBookingID: room.RoomBookingID,
              userID: userId
            }
          );

          if (response.status === 200) {
            successCount++;
          }
        } catch (error) {
          console.error(`Error modifying room ${room.RoomBookingID}:`, error);
          lastErrorMessage = error.response?.data?.message || error.message;
          errorCount++;
        }
      }

      if (successCount > 0 && errorCount === 0) {
        toast.success(workingRooms.length > 1
          ? `All ${successCount} bookings updated successfully!`
          : "Booking updated successfully!");
        await refetch();
      } else if (successCount > 0 && errorCount > 0) {
        toast.warning(`${successCount} bookings updated, ${errorCount} failed`);
        await refetch();
      } else {
        toast.error(lastErrorMessage || "Failed to update bookings");
      }
    } catch (error) {
      console.error("Error updating bookings:", error);
      toast.error(error.response?.data?.message || error.message || "Error while updating bookings");
    }
  };

  return (
    <div id="cancellation-page" style={{ marginTop: "3.5rem" }}>
      <div className="bg-white p-6 rounded-lg shadow-md p-15">
        <h2 className="text-xl font-bold mb-6 text-primary-600 text-center">
          Hotel Booking Cancellation
        </h2>

        {/* Cancellation Policy Section */}
        <div className="mb-6 bg-primary-50 p-2 rounded-lg border border-primary-200">

          {/* CANCELLATION TYPE DROPDOWN with LABEL */}
          <div className="d-flex flex-column align-items-center justify-content-center mb-4" style={{ marginTop: "10px" }}>
            <label htmlFor="cancelTypeDropdown" className="form-label fw-bold mb-2 text-primary">
              Cancellation Type:
            </label>
            <select
              id="cancelTypeDropdown"
              className="form-select w-auto"
              value={cancellationType}
              onChange={(e) => handleCancellationTypeChange(e.target.value)}
              disabled={hasPartialRoomCancellation}
            >
              <option value="" disabled>Select an option</option>

              {/* Full Cancellation - Show if NOT locked to partial room cancellation */}
              {!hasPartialRoomCancellation && (
                <option value="full">Full Cancellation</option>
              )}

              {/* Partial Room Cancellation - Show if applicable AND NOT locked to modification */}
              {((originalRooms.length > 1) || hasPartialRoomCancellation) && !hasPartialDayCancellation && (
                <option value="partial">Partial Room Cancellation</option>
              )}

              {/* Partial Day Cancellation - Show if applicable AND NOT locked to room partial */}
              {((bookingDuration > 1 && partialDayCancellationEligible) || hasPartialDayCancellation) && !hasPartialRoomCancellation && (
                <option value="modify">Partial Day Cancellation</option>
              )}

            </select>
            {/* Show lock message if user has already started a cancellation method */}
            {hasPartialRoomCancellation && (
              <small className="text-muted mt-2" style={{ fontStyle: 'italic' }}>
                <i className="bi bi-lock-fill me-1"></i>
                You have already started partial room cancellation. You can only continue with this method.
              </small>
            )}
            {hasPartialDayCancellation && !hasPartialRoomCancellation && (
              <small className="text-info mt-2" style={{ fontStyle: 'italic' }}>
                <i className="bi bi-info-circle-fill me-1"></i>
                You have started partial day cancellation. You can continue modifying dates or proceed with full cancellation.
              </small>
            )}
          </div>

          <h5 className="text-lg font-semibold mb-2">Cancellation Policy</h5>
          {/* DESKTOP CANCELLATION POLICY TABLE */}
          <div className="d-none d-md-block">
            <table className="table table-bordered cancellation-table">
              <thead>
                <tr>
                  <th>Sl#</th>
                  <th>Cancellation Window</th>
                  <th>Deduction</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {cancellationPolicy.map((policy) => (
                  <tr key={policy.id}>
                    <td>{policy.id}</td>
                    <td>{policy.window}</td>
                    <td style={policy.isCritical ? { color: "red" } : {}}>{policy.deduction}</td>
                    <td style={policy.isCritical ? { color: "red" } : {}}>{policy.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CANCELLATION POLICY CARDS */}
          <div className="d-block d-md-none">
            {cancellationPolicy.map((policy) => (
              <div key={policy.id} className="card mb-3 shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-2">#{policy.id}</h5>
                  <p><strong>Window:</strong> {policy.window}</p>
                  <p><strong>Deduction:</strong> <span style={policy.isCritical ? { color: 'red' } : {}}>{policy.deduction}</span></p>
                  <p><strong>Description:</strong> <span style={policy.isCritical ? { color: 'red' } : {}}>{policy.description}</span></p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {cancellationType ? (
          // SHOW all content when cancellationType is selected
          <div className="rounded-3 mt-4 mb-85">
            <h3 className="text-center p-5 mt-2">
              {cancellationType === "partial" ? "Select a Room to Cancel" : cancellationType === "modify" ? "Modify Booking" : "Your Booking Overview"}
            </h3>

            {/* Global Date Selector for Modify Booking */}
            {cancellationType === "modify" && (
              <div className="date-modify-container mb-5">

                <div className="date-selection-grid">
                  <div className="date-box">
                    <span className="date-label">Check-in</span>
                    <div className="date-picker-container">
                      <div
                        className="date-value-display"
                        onClick={() => setShowDatePicker(showDatePicker === 'checkin' ? null : 'checkin')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#007bff' }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        <span className="date-text">
                          {globalModifyDates.newCheckinDate ? convertDateInputToDMY(globalModifyDates.newCheckinDate) : 'Select Date'}
                        </span>
                      </div>
                      {showDatePicker === 'checkin' && (
                        <div className="date-picker-calendar">
                          <DayPicker
                            mode="single"
                            selected={globalModifyDates.newCheckinDate ? new Date(globalModifyDates.newCheckinDate) : null}
                            defaultMonth={(() => {
                              if (globalModifyDates.newCheckinDate) return new Date(globalModifyDates.newCheckinDate);
                              if (globalModifyDates.originalCheckinDate) return new Date(convertDMYtoDateInput(globalModifyDates.originalCheckinDate));
                              return new Date();
                            })()}
                            onSelect={(date) => {
                              if (date) {
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                const newCheckin = `${year}-${month}-${day}`;
                                setGlobalModifyDates(prev => ({ ...prev, newCheckinDate: newCheckin }));
                                setShowDatePicker(null);
                              }
                            }}
                            disabled={(date) => {
                              if (!globalModifyDates.originalCheckinDate || !globalModifyDates.originalCheckoutDate) {
                                return false;
                              }
                              const minDate = new Date(convertDMYtoDateInput(globalModifyDates.originalCheckinDate));
                              const maxDate = new Date(globalModifyDates.newCheckoutDate || convertDMYtoDateInput(globalModifyDates.originalCheckoutDate));
                              const limitDate = new Date(maxDate);
                              limitDate.setDate(limitDate.getDate() - 1);
                              return isBefore(date, minDate) || isAfter(date, limitDate);
                            }}
                            modifiers={{
                              inRange: (date) => {
                                if (!globalModifyDates.originalCheckinDate || !globalModifyDates.originalCheckoutDate) {
                                  return false;
                                }
                                const minDate = new Date(convertDMYtoDateInput(globalModifyDates.originalCheckinDate));
                                const maxDate = new Date(globalModifyDates.newCheckoutDate || convertDMYtoDateInput(globalModifyDates.originalCheckoutDate));
                                return isWithinInterval(date, { start: minDate, end: maxDate });
                              }
                            }}
                            modifiersStyles={{
                              inRange: { backgroundColor: '#90EE90', color: '#000' }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="date-arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </div>

                  <div className="date-box">
                    <span className="date-label">Check-out</span>
                    <div className="date-picker-container">
                      <div
                        className="date-value-display"
                        onClick={() => setShowDatePicker(showDatePicker === 'checkout' ? null : 'checkout')}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#007bff' }}>
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                          <polyline points="16 17 21 12 16 7"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                        <span className="date-text">
                          {globalModifyDates.newCheckoutDate ? convertDateInputToDMY(globalModifyDates.newCheckoutDate) : 'Select Date'}
                        </span>
                      </div>
                      {showDatePicker === 'checkout' && (
                        <div className="date-picker-calendar">
                          <DayPicker
                            mode="single"
                            selected={globalModifyDates.newCheckoutDate ? new Date(globalModifyDates.newCheckoutDate) : null}
                            defaultMonth={(() => {
                              if (globalModifyDates.newCheckoutDate) return new Date(globalModifyDates.newCheckoutDate);
                              if (globalModifyDates.originalCheckoutDate) return new Date(convertDMYtoDateInput(globalModifyDates.originalCheckoutDate));
                              return new Date();
                            })()}
                            onSelect={(date) => {
                              if (date) {
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                const newCheckout = `${year}-${month}-${day}`;
                                setGlobalModifyDates(prev => ({ ...prev, newCheckoutDate: newCheckout }));
                                setShowDatePicker(null);
                              }
                            }}
                            disabled={(date) => {
                              if (!globalModifyDates.originalCheckinDate || !globalModifyDates.originalCheckoutDate) {
                                return false;
                              }
                              const checkinDateStr = globalModifyDates.newCheckinDate || convertDMYtoDateInput(globalModifyDates.originalCheckinDate);
                              const [cy, cm, cd] = checkinDateStr.split('-').map(Number);
                              const minDate = new Date(cy, cm - 1, cd);
                              minDate.setDate(minDate.getDate() + 1);
                              const maxDateStr = convertDMYtoDateInput(globalModifyDates.originalCheckoutDate);
                              const [my, mm, md] = maxDateStr.split('-').map(Number);
                              const maxDate = new Date(my, mm - 1, md);
                              return isBefore(date, minDate) || isAfter(date, maxDate);
                            }}
                            modifiers={{
                              inRange: (date) => {
                                if (!globalModifyDates.originalCheckinDate || !globalModifyDates.originalCheckoutDate) {
                                  return false;
                                }
                                const checkinDateStr = globalModifyDates.newCheckinDate || convertDMYtoDateInput(globalModifyDates.originalCheckinDate);
                                const [cy, cm, cd] = checkinDateStr.split('-').map(Number);
                                const minDate = new Date(cy, cm - 1, cd);
                                const maxDateStr = convertDMYtoDateInput(globalModifyDates.originalCheckoutDate);
                                const [my, mm, md] = maxDateStr.split('-').map(Number);
                                const maxDate = new Date(my, mm - 1, md);
                                return isWithinInterval(date, { start: minDate, end: maxDate });
                              }
                            }}
                            modifiersStyles={{
                              inRange: { backgroundColor: '#90EE90', color: '#000' }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Desktop Table */}
            <div className="d-none d-md-block">
              <table className="table table-bordered w-100 cancellation-table">
                <thead>
                  <tr>
                    <th>Sl.No</th>
                    <th>Room Type</th>
                    <th>Check-in Date</th>
                    <th>Check-out Date</th>
                    <th>No.Of Days</th>
                    <th>No. of Adults</th>
                    <th>Price Per Day</th>
                    <th>Extra Person Rate</th>
                    <th>Extra Person</th>
                    <th>Sub Total Amount</th>
                    <th>GST Amount</th>
                    <th>Total Amount</th>
                    {cancellationType === "partial" && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {workingRooms.map((room, index) => {
                    const inDate = parseDMY(room.CheckinDate);
                    const outDate = parseDMY(room.CheckOutDate);
                    const numDays = (outDate - inDate) / (1000 * 60 * 60 * 24);
                    return (
                      <tr key={room.uniqueId}>
                        <td>{index + 1}</td>
                        <td>{room.RoomTypeName[0]}</td>
                        <td>
                          {cancellationType === "modify"
                            ? (globalModifyDates.newCheckinDate ? convertDateInputToDMY(globalModifyDates.newCheckinDate) : room.CheckinDate)
                            : room.CheckinDate
                          }
                        </td>
                        <td>
                          {cancellationType === "modify"
                            ? (globalModifyDates.newCheckoutDate ? convertDateInputToDMY(globalModifyDates.newCheckoutDate) : room.CheckOutDate)
                            : room.CheckOutDate
                          }
                        </td>
                        <td>
                          {cancellationType === "modify" && globalModifyDates.newCheckinDate && globalModifyDates.newCheckoutDate
                            ? (() => {
                              const newInDate = new Date(globalModifyDates.newCheckinDate);
                              const newOutDate = new Date(globalModifyDates.newCheckoutDate);
                              return (newOutDate - newInDate) / (1000 * 60 * 60 * 24);
                            })()
                            : numDays
                          }
                        </td>
                        <td>{room.NoOfAdult}</td>
                        <td>₹{room.PricePerDay}</td>
                        <td>₹{room.ExtraPersonRate}</td>
                        <td>{room.AllowedExtraPerson}</td>
                        <td>
                          {numDays} x (₹{room.PricePerDay}
                          {room.ExtraPersonRate > 0 ? ` + ₹${room.ExtraPersonRate}` : ""}
                          ) = ₹{
                            (
                              numDays * (room.PricePerDay + (room.ExtraPersonRate || 0))
                            ).toFixed(2)
                          }
                        </td>
                        <td>
                          {taxRate !== null
                            ? `₹${((numDays * (room.PricePerDay + (room.ExtraPersonRate || 0))) * taxRate).toFixed(2)}`
                            : 'Loading...'
                          }
                        </td>

                        <td>₹{(
                          (numDays * (room.PricePerDay + (room.ExtraPersonRate || 0))) +
                          ((numDays * (room.PricePerDay + (room.ExtraPersonRate || 0))) * (taxRate || 0))
                        ).toFixed(2)}</td>
                        {cancellationType === "partial" && (
                          <td>
                            {cancelledRoomIds.includes(room.RoomBookingID) ? (
                              <button className="btn btn-secondary" disabled>Cancelled</button>
                            ) : (
                              <button
                                className="btn btn-danger"
                                onClick={() =>
                                  handleRoomCancellation((room.PricePerDay + room.ExtraPersonRate) * totalDaysBooked, room.RoomType, room.RoomBookingID)
                                }
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        )}

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="d-block d-md-none">
              {workingRooms.map((room, index) => (
                <div className="card mb-3 shadow-sm" key={room.uniqueId}>
                  <div className="card-body">
                    <h5 className="card-title">Room {index + 1}</h5>
                    <p><strong>Room Type:</strong> {room.RoomTypeName[0]}</p>
                    <p><strong>Check-in Date:</strong> {room.CheckinDate}</p>
                    <p><strong>Check-out Date:</strong> {room.CheckOutDate}</p>
                    <p><strong>No. of Rooms:</strong> 1</p>
                    <p><strong>No. of Adults:</strong> {room.NoOfAdult}</p>
                    <p><strong>No. of Children:</strong> {room.NoOfChild}</p>
                    <p><strong>Price Per Day:</strong> ₹{room.PricePerDay}</p>
                    <p><strong>Extra Person Rate:</strong> ₹{room.ExtraPersonRate}</p>
                    <p><strong>Extra Person:</strong> {room.AllowedExtraPerson}</p>
                    <p><strong>Total Amount:</strong> ₹{room.ExtraPersonRate + room.PricePerDay}</p>

                    {cancellationType === "partial" && (
                      cancelledRoomIds.includes(parseInt(room.RoomType)) ? (
                        <button className="btn btn-secondary w-100 mt-2" disabled>Cancelled</button>
                      ) : (
                        <button
                          className="btn btn-danger w-100 mt-2"
                          onClick={() =>
                            handleRoomCancellation(room.PricePerDay + room.ExtraPersonRate, room.RoomType, room.RoomBookingID)
                          }
                        >
                          Cancel
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Full Cancellation Button */}
            {cancellationType === "full" && workingRooms.length > 0 && !partialCancellationStarted && (
              <div className="mt-4 d-flex justify-content-center">
                <button
                  onClick={handleOpenModal}
                  className="btn btn-danger"
                  style={{ minWidth: "200px" }}
                >
                  {workingRooms.length > 1 ? "Cancel All Rooms" : "Cancel Room"}
                </button>
              </div>
            )}

            {/* Modify Booking Button */}
            {cancellationType === "modify" && workingRooms.length > 0 && (
              <div className="mt-4 d-flex justify-content-center">
                <button
                  onClick={handleUpdateAllBookings}
                  className="btn btn-primary"
                  style={{ minWidth: "200px" }}
                >
                  {workingRooms.length > 1 ? "Update All Bookings" : "Update Booking"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-muted my-5">
            <em>Please select a cancellation type above to view booking details and proceed.</em>
          </div>
        )}

      </div>

      {/* Full Cancellation Confirmation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Booking Cancellation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingCharges ? (
            <div>Loading cancellation details...</div>
          ) : cancellationInfo ? (
            <>
              <p style={{ marginBottom: "10px", fontSize: "14px", color: "#555" }}>
                Total amount paid: <strong>₹{cancellationInfo.totalAmount}</strong> (includes {(taxRate * 100)}% GST)
              </p>
              <p style={{ fontSize: "15px", color: "#000" }}>
                Cancellation charge: <strong style={{ color: '#e74c3c' }}>
                  ₹{cancellationInfo.cancellationCharge}
                </strong>
              </p>
              <p style={{ fontSize: "15px", color: "#000" }}>
                Refund amount: <strong style={{ color: 'green' }}>
                  ₹{cancellationInfo.refundAmount}
                </strong>
              </p>
              <p style={{ marginTop: "15px", fontSize: "13px", color: "#777" }}>
                Are you sure you want to proceed with cancelling the entire booking?
              </p>
            </>
          ) : (
            <div>Error loading cancellation info</div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Go Back
          </Button>
          <Button
            variant="danger"
            onClick={handleFullCancellation}
            disabled={loadingCharges}
          >
            Yes, Cancel All Rooms
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Partial Cancellation Confirmation Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Room Cancellation</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p style={{ marginBottom: "10px", fontSize: "14px", color: "#555" }}>
            Total amount paid: <strong>
              {taxRate !== null
                ? `₹${(selectedPrice * (1 + taxRate)).toFixed(2)}`
                : 'Calculating...'
              }
            </strong>
            {taxRate !== null ? `(includes ${(taxRate * 100)}% GST)` : ''}
          </p>
          <p style={{ fontSize: "15px", color: "#000" }}>
            Cancellation charge: <strong style={{ color: '#e74c3c' }}>
              {cancellationAmount !== null ? `₹${cancellationAmount}` : "Loading..."}
            </strong>
          </p>
          <p style={{ fontSize: "15px", color: "#000" }}>
            Refund amount: <strong style={{ color: 'green' }}>
              {cancellationAmount !== null ? `₹${refundAmount}` : "Loading..."}
            </strong>
          </p>
          <p style={{ marginTop: "15px", fontSize: "13px", color: "#777" }}>
            Are you sure you want to proceed with cancelling this room?
          </p>
        </Modal.Body>
        {/*   */}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Go Back
          </Button>
          <Button
            variant="danger"
            onClick={confirmCancellation}
            disabled={cancellationAmount === null}
          >
            Yes, Cancel Room
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};
export default Cancellation;

